const { m } = require('minthril');
const routemeup = require('routemeup').default;
const { getProducts, getCollections, getProductByHandle, getLocalization, getCart, addToCart, setCountry, updateCartLine, removeCartLine, getCheckoutUrl } = require('../../business');
const { Button } = require('../button');
const { Pages } = require('../pages');

function App({ attrs: { redraw } }) {
  let products = [];
  let collections = [];
  let countries = [];
  let selectedCountry = window.localStorage.getItem('midengine-country-code') || '';
  let selectedProduct = null;
  let cartCount = 0;
  let cartQuantities = {};
  let cart = null;
  let isLoading = true;
  let errorMessage = null;

  function showError(reason) {
    errorMessage = reason.message;
    redraw();
  }

  function updateDocumentMetadata(pathname) {
    const product = selectedProduct;
    const isProduct = Boolean(product);
    const matchedRoute = routemeup({
      '/': () => true,
      '/products': () => true,
      '/about': () => true,
      '/cart': () => true,
      '/contact': () => true,
      '/policies/privacy-policy': () => true,
      '/pages/about-us': () => true,
      '/pages/contact': () => true,
      '/pages/data-sharing-opt-out': () => true,
      '/collections': () => true,
      '/collections/all': () => true,
      '/collections/:handle': () => true,
      '/products/:handle': () => true
    }, { url: pathname });
    const collectionRoute = routemeup({
      '/collections/:handle': tokens => tokens.handle
    }, { url: pathname });
    const collectionHandle = pathname === '/collections/all'
      ? null
      : collectionRoute ? collectionRoute.controller(collectionRoute.tokens) : null;
    const selectedCollection = collections.find(collection => collection.handle === collectionHandle);
    const isNotFound = !matchedRoute || (collectionHandle && !selectedCollection);
    const pageTitle = isNotFound
      ? 'Page Not Found | Mid Engine Co'
      : isProduct
      ? `${product.title} | Mid Engine Co`
      : pathname === '/products' || pathname === '/collections/all' ? 'Parts Catalogue | Mid Engine Co'
        : pathname === '/about' ? 'About Mid Engine Co'
          : pathname === '/contact' ? 'Contact Mid Engine Co'
            : pathname === '/policies/privacy-policy' ? 'Privacy policy | Mid Engine Co'
              : pathname === '/pages/data-sharing-opt-out' ? 'Your Privacy Choices | Mid Engine Co'
                : pathname === '/collections' ? 'Collections | Mid Engine Co'
                  : selectedCollection ? `${selectedCollection.title} | Mid Engine Co`
            : 'Mid Engine Co | Specialist Automotive Parts';
    const description = isNotFound
      ? 'The page you requested could not be found.'
      : isProduct
      ? product.description || `Specialist parts and upgrades for ${product.title}.`
      : 'Specialist components, considered upgrades, and hard-to-find pieces for mid-engine machines.';

    document.title = pageTitle;
    setMetaTag('description', description);
    setMetaTag('og:title', pageTitle, 'property');
    setMetaTag('og:description', description, 'property');
    setMetaTag('og:type', isProduct ? 'product' : 'website', 'property');
    setMetaTag('og:image', product?.featuredImage?.url || `${window.location.origin}/images/Gallardo.webp`, 'property');
    setLinkTag('canonical', `${window.location.origin}${pathname}`);

    if (isNotFound) setMetaTag('robots', 'noindex');
    else removeMetaTag('robots');
  }

  function selectProductFromPath(pathname) {
    const normalizedPath = pathname.replace(/\/$/, '') || '/';
    const route = routemeup({
      '/': () => null,
      '/products': () => null,
      '/about': () => null,
      '/cart': () => null,
      '/products/:handle': tokens => tokens.handle
    }, { url: normalizedPath });
    const handle = route ? route.controller(route.tokens) : null;
    selectedProduct = products.find(product => product.handle === handle) || null;

    return Boolean(handle && selectedProduct);
  }

  function updateCartQuantities(cart) {
    cartQuantities = {};
    cart?.lines?.nodes?.forEach(line => {
      const variantId = line.merchandise?.id;
      if (variantId) cartQuantities[variantId] = line.quantity;
    });
  }

  function getProductHandleFromPath(pathname) {
    const normalizedPath = pathname.replace(/\/$/, '') || '/';
    const route = routemeup({
      '/products/:handle': tokens => tokens.handle
    }, { url: normalizedPath });
    return route ? route.controller(route.tokens) : null;
  }

  function navigate(path) {
    const url = new URL(path, window.location.origin);
    window.history.pushState({}, '', `${url.pathname}${url.search}${url.hash}`);
    selectProductFromPath(url.pathname);
    window.scrollTo(0, 0);
    redraw();
    loadProductForPath(url.pathname);

    if (url.hash) {
      setTimeout(() => document.querySelector(url.hash)?.scrollIntoView(), 0);
    }
  }

  async function loadProductForPath(pathname) {
    const handle = getProductHandleFromPath(pathname);
    if (!handle) return;

    try {
      const product = await getProductByHandle(handle);
      if (!product) return;

      products = products.some(item => item.id === product.id)
        ? products.map(item => item.id === product.id ? product : item)
        : [...products, product];
      selectedProduct = product;
      redraw();
    } catch (reason) {
      showError(reason);
    }
  }

  function handleNavigation(event) {
    const target = event.target;
    const element = target.nodeType === Node.ELEMENT_NODE ? target : target.parentElement;
    const control = element?.closest('button, input, select, textarea, option, [contenteditable="true"]');

    if (control) return;

    const anchor = element?.closest('a');

    if (!anchor || anchor.origin !== window.location.origin || event.metaKey || event.ctrlKey) {
      return;
    }

    event.preventDefault();
    navigate(`${anchor.pathname}${anchor.search}${anchor.hash}`);
  }

  async function loadProducts() {
    try {
      const [loadedProducts, loadedCollections, loadedCart, loadedCountries] = await Promise.all([
        getProducts(100),
        getCollections(),
        getCart(),
        getLocalization()
      ]);
      products = loadedProducts;
      collections = loadedCollections;
      cart = loadedCart;
      countries = loadedCountries.countries;
      if (!selectedCountry) selectedCountry = loadedCountries.currentCountry.isoCode;
      cartCount = loadedCart?.totalQuantity || 0;
      updateCartQuantities(loadedCart);
      await loadProductForPath(window.location.pathname);
    } catch (reason) {
      errorMessage = reason.message;
    } finally {
      isLoading = false;
      redraw();
    }
  }

  async function changeCountry(countryCode) {
    try {
      selectedCountry = countryCode;
      isLoading = true;
      redraw();
      cart = await setCountry(countryCode || null);
      const [loadedProducts, loadedCollections] = await Promise.all([
        getProducts(100),
        getCollections()
      ]);
      products = loadedProducts;
      collections = loadedCollections;
      cartCount = cart?.totalQuantity || 0;
      updateCartQuantities(cart);
    } catch (reason) {
      showError(reason);
    } finally {
      isLoading = false;
      redraw();
    }
  }

  async function addProduct(variantId, quantity = 1) {
    try {
      const updatedCart = await addToCart(variantId, quantity);
      cart = updatedCart;
      cartCount = updatedCart.totalQuantity;
      updateCartQuantities(updatedCart);
      redraw();
    } catch (reason) {
      showError(reason);
    }
  }

  async function updateLine(line, quantity) {
    try {
      cart = await updateCartLine(line.id, quantity);
      cartCount = cart.totalQuantity;
      updateCartQuantities(cart);
      redraw();
    } catch (reason) { showError(reason); }
  }

  async function removeLine(line) {
    try {
      cart = await removeCartLine(line.id);
      cartCount = cart.totalQuantity;
      updateCartQuantities(cart);
      redraw();
    } catch (reason) { showError(reason); }
  }

  async function checkout() {
    try {
      window.location.href = await getCheckoutUrl();
    } catch (reason) {
      showError(reason);
    }
  }

  window.addEventListener('popstate', () => {
    selectProductFromPath(window.location.pathname);
    redraw();
    loadProductForPath(window.location.pathname);
  });

  loadProducts();

  return {
    view: () => {
      updateDocumentMetadata(window.location.pathname);

      return m(`main.app${selectedProduct ? '.app--product' : ''}`, { onclick: handleNavigation },
      m('header.header',
        m('a.header__brand', { href: '/' },
          m('img', { src: '/images/mec-logo-basic-small.webp', alt: 'Mid Engine Co' })
        ),
        m('nav.header__nav',
          m('a', { href: '/' }, 'Home'),
          m('a', { href: '/products' }, 'Products'),
          m('a', { href: '/about' }, 'About'),
          m('a', { href: 'https://account.midengineco.com/' }, 'Account'),
          m('a.header__cart', { href: '/cart' }, `Cart (${cartCount})`),
          countries.length ? m('label.header__country',
            m('span.header__currency-code',
              countries.find(country => country.isoCode === selectedCountry)?.currency.isoCode
                || countries[0].currency.isoCode
            ),
            m('select', {
              value: selectedCountry,
              onchange: event => changeCountry(event.target.value)
            }, countries.map(country => m('option', { value: country.isoCode }, `${country.name} · ${country.currency.isoCode}`)))
          ) : null
        ),
      ),
      errorMessage ? m('p.error', errorMessage) : null,
      isLoading
        ? m('p.products-status', 'Loading products…')
        : m(Pages, {
          pathname: window.location.pathname,
          products,
          collections,
          selectedProduct,
          cart,
          onAdd: addProduct,
          onUpdate: updateLine,
          onRemove: removeLine,
          onCheckout: checkout,
          cartQuantities,
          redraw
        }),
      m('footer.footer',
        m('a', { href: '/contact' }, 'Contact'),
        m('a', { href: '/policies/privacy-policy' }, 'Privacy policy'),
        m('span', 'MID ENGINE CO · Quality parts for your machine')
      )
      );
    }
  };
}

function setMetaTag(name, content, attribute = 'name') {
  let element = document.head.querySelector(`meta[${attribute}="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function removeMetaTag(name) {
  document.head.querySelector(`meta[name="${name}"]`)?.remove();
}

function setLinkTag(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

module.exports = { App };
